import { useCallback, useEffect, useState } from 'react';
import getCardPhotoUrl from '@alltrails/core/utils/getCardPhotoUrl';
import getCustomRoutePhotoUrl from '@alltrails/core/utils/getCustomRoutePhotoUrl';
import type CardType from '@alltrails/types/CardType';
import PhotoSizeDimensions from '@alltrails/types/PhotoSizeDimensions';

const useCardPhotos = (
  cardType: CardType,
  id: number,
  size: PhotoSizeDimensions,
  maxPhotos: number,
  isCustomRoute?: boolean,
  baseUrl: string = ''
) => {
  const [photos, setPhotos] = useState<string[]>([]);
  const [photosLoaded, setPhotosLoaded] = useState(false);

  useEffect(() => {
    if (maxPhotos < 1) {
      setPhotos([]);
      setPhotosLoaded(true);
    } else {
      const initialPhoto = isCustomRoute
        ? getCustomRoutePhotoUrl({ id, index: 0, size, baseUrl })
        : getCardPhotoUrl({ cardType, id, index: 0, size, baseUrl });
      if (maxPhotos === 1) {
        setPhotos([initialPhoto]);
        setPhotosLoaded(true);
      } else {
        const fillerPhotos = Array(maxPhotos - 1).fill('') as string[];
        setPhotos([initialPhoto, ...fillerPhotos]);
        setPhotosLoaded(false);
      }
    }
  }, [size, maxPhotos, cardType, id, baseUrl, isCustomRoute]);

  const loadPhotos = useCallback(() => {
    // For custom routes, we don't call loadPhotos as we only show one photo
    if (!photosLoaded && !isCustomRoute) {
      setPhotosLoaded(true);

      const urls = photos.map((_, index) => {
        const url = getCardPhotoUrl({ cardType, id, index, size, baseUrl });
        // pre-load images by browser
        try {
          new Image().src = url;
        } catch {
          // silently catch
        }
        return url;
      });

      setPhotos(urls);
    }
  }, [baseUrl, cardType, id, photos, photosLoaded, size, isCustomRoute]);

  return { photos, loadPhotos };
};

export default useCardPhotos;
